import Input from 'components/atoms/Input';
import Button from 'components/atoms/Button';
import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import api from 'services/api';
import { useContext } from 'react';
import { GlobalContext } from 'Context/GlobalContext/AppContext';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import FormTitle from './FormTitle';
import FormItemsWrapper from './FormItemsWrapper';
import SelectWrapper from './SelectWrapper';
import FormDescription from './FormDescription';
import FormSelect from './FormSelect';
import FormCheck from './FormCheck';
import FormDatePicker from './FormDatePicker';
import validateCaracteresAmount from 'components/helpers/validateCaracteresAmount';
import moment from 'moment';
import './styles.scss';
import sliceText from 'components/helpers/sliceText';

//Convert incoming classification to dropbox format
function normalizeClassification(options) {
    return options
        .filter((option) => option.Ativo)
        .map((option) => ({
            label: option.Titulo,
            value: option.ID,
        }));
}

const initialDocumentState = {
    ID: 0,
    NRDocumento: '',
    Titulo: '',
    Descricao: '',
    Tag: '',
    AgrupamentoId: '',
    UltimaVersao: true,
    Versao: '',
    DTCadastro: moment().format('YYYY-MM-DD'),
    Ordem: '',
    Ativo: true,
};

const Form = ({ Title, selectedDocument, relatedData, tipoDoc }) => {
    const { setClassificationModal, setLoading, user, setResponseMsg, setRelatedDocumentsModal, tags } = useContext(GlobalContext);
    const [classificacoes, setClassificacoes] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedClassificacao, setSelectedClassificacao] = useState('');
    const [document, setDocument] = useState(selectedDocument || initialDocumentState);
    const [toRefresh, setToRefresh] = useState(null);
    const [descricao, setDescricao] = useState('');
    const [existingFile, setExistingFile] = useState(selectedDocument ? selectedDocument.NomeArquivo.length > 0 : false);
    const descricaoRef = useRef(null);
    const arquivoRef = useRef(null);
    const navigate = useNavigate();

    const existingFileClass = classNames({
        fileExisting__label: true,
        'fileExisting__label--visible': existingFile,
    });

    const receivedClassificacao = useCallback(
        //Return classification name
        (id) => {
            if (!id || tipoDoc !== 1) return '';
            return classificacoes.reduce((received, classificacao) => {
                if (classificacao.value === id) {
                    return classificacao;
                }
                return { ...received };
            }, {});
        },
        [classificacoes, tipoDoc],
    );

    useEffect(() => {
        setLoading(true);

        api
            .getClassificacoes()
            .then((response) => setClassificacoes(normalizeClassification(response.data.Item)))
            .catch((error) => {
                console.error(error);
                setResponseMsg({ isVisible: true, error: true, msg: error.message });
            })
            .finally(() => setLoading(false));
    }, [setLoading, setResponseMsg, toRefresh]);

    //Execute only if editing mode
    useEffect(() => {
        if (document.ID > 0) {
            setDescricao(document.Descricao);
            setSelectedClassificacao(receivedClassificacao(document.AgrupamentoId));
            setSelectedTags(document.Tags);
        }
    }, [document.Descricao, document.Tags, document.AgrupamentoId, document.ID, setDocument, receivedClassificacao, setLoading, setResponseMsg]);

    function handleOnChange(e) {
        let value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        if (e.target.type === 'number') {
            value = e.target.value.length <= 3 ? value : 999;
        }
        setDocument((prevState) => ({ ...prevState, [e.target.name]: value }));
    }

    function handleChangeFile(e) {
        const value = e.target.files[0] ? false : true;
        setExistingFile(value);
    }

    function handleTagsLimit(tags) {
        if (tags.length > 5) {
            setSelectedTags((prevTags) => prevTags);
        } else {
            setSelectedTags(tags);
        }
    }

    function appendArray(form_data, values, name) {
        if (!values && name)
            form_data.append(name, '');
        else {
            if (typeof values == 'object') {
                for (let key in values) {
                    if (typeof values[key] == 'object')
                        appendArray(form_data, values[key], name + '[' + key + ']');
                    else
                        form_data.append(name + '[' + key + ']', values[key]);
                }
            } else
                form_data.append(name, values);
        }

        return form_data;
    }

    async function onSubmit(e) {
        e.preventDefault();

        //Create formdata to send
        try {
            const formData = new FormData();

            formData.set('ID', document.ID);
            formData.set('NRDocumento', document.NRDocumento);
            formData.set('Titulo', document.Titulo);

            if (validateCaracteresAmount('Descrição', descricaoRef.current?.getText(), 1002)) {
                formData.set('Descricao', descricaoRef.current.getText());
            }

            if (selectedTags.length <= 0) {
                throw new Error('O campo de Tags não pode ficar vazio.');
            }

            formData.set('Tag', selectedTags?.map((tag) => tag.value).toString());
            formData.set('TipoDoc', tipoDoc || document.TipoDoc);

            if (!selectedClassificacao.value && (document.TipoDoc === 1 || tipoDoc === 1)) {
                throw new Error('O campo de Classificação não pode ficar vazio.');
            }

            formData.set('AgrupamentoId', tipoDoc === 1 ? selectedClassificacao.value : 1);
            formData.set('UltimaVersao', document.UltimaVersao);
            formData.set('Versao', document.Versao);
            formData.set('DTCadastro', moment(document.DTCadastro).format('YYYY-MM-DD'));
            formData.set('Ordem', document.Ordem);
            formData.set('Ativo', document.Ativo);

            const nameOfFiles = [...relatedData.files.map((file) => file.name), ...relatedData.links.map((file) => file.Nome)];

            if (arquivoRef.current.files[0]) {
                if (nameOfFiles.find((filename) => filename === arquivoRef.current.files[0].name)) {
                    throw new Error('O arquivo anexado ao Documento não pode ter o mesmo nome de um Arquivo Relacionado.');
                }
            } else {
                if (nameOfFiles.find((filename) => filename === document.NomeArquivo)) {
                    throw new Error('O arquivo anexado ao Documento não pode ter o mesmo nome de um Arquivo Relacionado.');
                }
            }

            formData.set('Arquivo', arquivoRef.current.files[0]);

            relatedData.files.forEach((file) => {
                formData.append('Arquivo', file, file.name);
            });

            appendArray(formData, relatedData.links, 'Links');
            appendArray(formData, relatedData.documents, 'Documentos');
            //relatedData.links.forEach(olink => {
            //    formData.append('Links', new Array(){ olink);
            //});
            //relatedData.documents.forEach(odoc => {
            //    formData.append('Documentos', odoc);
            //});

            setLoading(true);
            const { data } = await api.postDocument(user, formData);
            if (data.ID > 0) {
                setResponseMsg({ isVisible: true, error: false, msg: 'Documento salvo com Sucesso!' });
                setLoading(false);
                navigate('/');
            } else {
                if (data.Mensagem.includes('Ghost')) {
                    throw new Error('O formato de arquivo enviado é inválido.');
                }
                throw new Error(data.Mensagem);
            }
        } catch (error) {
            console.error(error);
            setResponseMsg({ isVisible: true, error: true, msg: error.message });
            setLoading(false);
        }
    }

    return (
        <>
            <form onSubmit={onSubmit} className="form" encType="multipart/form-data">
                <FormTitle>{Title}</FormTitle>
                <FormItemsWrapper>
                    <Input
                        maxLength={tipoDoc === 1 ? '20' : '50'}
                        name="NRDocumento"
                        label="Código / Número"
                        type="text"
                        value={document.NRDocumento}
                        onChange={handleOnChange}
                    />
                    <Input maxLength="10" name="Versao" label="Versão" onChange={handleOnChange} value={document.Versao} />
                    <Input min={0} max={999} type="number" name="Ordem" label="Ordem" onChange={handleOnChange} value={document.Ordem} />
                </FormItemsWrapper>
                <Input maxLength="255" name="Titulo" label="Título da Norma" onChange={handleOnChange} value={document.Titulo} />
                <FormDescription value={descricao} ref={descricaoRef} onChange={setDescricao} />
                <FormItemsWrapper>
                    <SelectWrapper>
                        <FormSelect
                            isDisabled={!(tipoDoc === 1)}
                            options={classificacoes}
                            value={selectedClassificacao}
                            onChange={setSelectedClassificacao}
                            classNamePrefix="select"
                            title="Classificação"
                            placeholder="Selecione uma classificação"
                        />
                        {tipoDoc === 1 && (
                            <Button visual="edit" type="button" onClick={() => setClassificationModal({ isVisible: true, callback: setToRefresh })} />
                        )}
                    </SelectWrapper>
                    <Button visual="related" type="button" onClick={() => setRelatedDocumentsModal({ isVisible: true })}>
                        Documentos Relacionados
          </Button>
                </FormItemsWrapper>
                <SelectWrapper>
                    <FormSelect
                        options={tags}
                        value={selectedTags}
                        onChange={handleTagsLimit}
                        classNamePrefix="selectTag"
                        title="Tags"
                        placeholder="Digite uma ou mais tags"
                        isMulti
                        creatable
                    />
                </SelectWrapper>
                <FormItemsWrapper>
                    <div className="form__checkWrapper">
                        <FormCheck name="UltimaVersao" onChange={handleOnChange} value={document.UltimaVersao}>
                            Última Versão?
            </FormCheck>
                        <FormCheck name="Ativo" onChange={handleOnChange} value={document.Ativo}>
                            Ativo
            </FormCheck>
                    </div>
                    <FormDatePicker onChange={handleOnChange} value={moment(document.DTCadastro).format('YYYY-MM-DD')} required>
                        Data de Cadastro
          </FormDatePicker>
                </FormItemsWrapper>
                <div className="form__buttonWrapper">
                    <div className="file__existing">
                        <label className={existingFileClass}>{existingFile && sliceText(document?.NomeArquivo, 0, 20)}</label>
                        <input
                            ref={arquivoRef}
                            required={document.ID > 0 ? false : true}
                            type="file"
                            name="Arquivo"
                            className="form__file"
                            accept=".pdf"
                            onChange={handleChangeFile}
                        />
                    </div>
                    <div className="form__buttons">
                        <Button tag={Link} to="/" visual="cancel">
                            Cancelar
            </Button>
                        <Button visual="save">Salvar</Button>
                    </div>
                </div>
            </form>
        </>
    );
};

export default Form;
